import React, { useState, useMemo } from 'react';
import { TrophyIcon } from '../icons/InterfaceIcons';

// FIX: Add explicit types for props to allow 'key' prop in map.
const ResultCard: React.FC<{ result: any }> = ({ result }) => (
    <div className="flex-shrink-0 w-48 text-center bg-brand-surface border border-brand-border rounded-xl overflow-hidden group transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
        <div className="relative">
            <img 
                src={result.url} 
                alt={result.name} 
                className="w-full h-48 object-cover" 
                onError={(e) => { e.currentTarget.src = 'https://i.pravatar.cc/300'; }}
            />
            <div className="absolute top-2 right-2 bg-brand-accent-amber text-white text-xs font-bold px-2 py-1 rounded-full">
                AIR {result.air}
            </div>
        </div>
        <div className="p-3">
            <p className="font-bold text-sm text-brand-text-primary truncate">{result.name}</p>
            <p className="text-xs text-brand-text-secondary mt-1 truncate">{result.program}</p>
        </div>
    </div>
);

export const RecentAchieversCard = ({ examResults }) => {
    const examNames = Object.keys(examResults);
    const [selectedExam, setSelectedExam] = useState(examNames[0] || '');
    
    const years = useMemo(() => {
        return selectedExam && examResults[selectedExam] 
            ? Object.keys(examResults[selectedExam]).sort((a, b) => Number(b) - Number(a)) 
            : [];
    }, [selectedExam, examResults]);

    const [selectedYear, setSelectedYear] = useState(years[0] || '');

    // Effect to update selected year when exam changes
    React.useEffect(() => {
        const availableYears = selectedExam && examResults[selectedExam] 
            ? Object.keys(examResults[selectedExam]).sort((a, b) => Number(b) - Number(a))
            : [];
        setSelectedYear(availableYears[0] || '');
    }, [selectedExam, examResults]);

    const results = useMemo(() => {
        if (selectedExam && selectedYear && examResults[selectedExam] && examResults[selectedExam][selectedYear]) {
            return Object.values(examResults[selectedExam][selectedYear]).sort((a, b) => Number(a.air) - Number(b.air));
        }
        return [];
    }, [selectedExam, selectedYear, examResults]);

    if (examNames.length === 0) {
        return null; // Don't render if there's no data
    }

    return (
        <div className="bg-brand-surface border border-brand-border rounded-xl p-6 shadow-card">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
                <div className="flex items-center gap-3">
                    <TrophyIcon className="w-8 h-8 text-brand-accent-amber" />
                    <div>
                        <h3 className="font-bold text-xl text-brand-text-primary">Celebrating Our Achievers</h3>
                        <p className="text-sm text-brand-text-secondary">Top performers from recent exams.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={selectedExam}
                        onChange={(e) => setSelectedExam(e.target.value)}
                        className="block w-full sm:w-auto pl-3 pr-8 py-2 text-sm border-brand-border focus:outline-none focus:ring-brand-primary focus:border-brand-primary rounded-md bg-brand-bg text-brand-text-primary"
                    >
                        {examNames.map(name => <option key={name} value={name}>{name.replace('_', ' ')}</option>)}
                    </select>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className="block w-full sm:w-auto pl-3 pr-8 py-2 text-sm border-brand-border focus:outline-none focus:ring-brand-primary focus:border-brand-primary rounded-md bg-brand-bg text-brand-text-primary"
                    >
                        {years.map(year => <option key={year} value={year}>{year.replace('_', ' ')}</option>)}
                    </select>
                </div>
            </div>
            
            <div className="flex overflow-x-auto space-x-4 pb-4 -mx-6 px-6">
                {results.length > 0 ? (
                    results.map((result, index) => <ResultCard key={`${selectedYear}-${result.name}-${index}`} result={result} />)
                ) : (
                    <div className="w-full text-center py-10">
                        <p className="text-brand-text-secondary">No results available for this selection.</p>
                    </div>
                )}
            </div>
        </div>
    );
};